import React, { useEffect } from 'react';

// Legal hub — Terms of Use, Rewards Terms, Privacy, Responsible Gambling.
// Modeled on the sharkz.gg legal pages, adapted to how SNUTZY actually works
// (Twitch OAuth, Shuffle affiliate rewards, fraud fingerprinting, lossback
// screenshots). Drafted copy: have it reviewed before treating it as final.
// Reached via /legal (also /terms, /privacy, /responsible-gambling, which
// scroll to their section).

const LAST_UPDATED = 'August 2026';

const sections = [
  {
    id: 'terms',
    title: 'Terms of Use',
    body: [
      'snutzy.gg is the community site for the SNUTZY stream, partnered with Shuffle. It hosts leaderboards, predictions, giveaways, points and other rewards for viewers who play on Shuffle under guild SNUTZY. By using the site you agree to these terms.',
      'Play fair: one account per person, no bots or scripts, no alternate accounts, no entering rewards on behalf of someone else, and no evading bans or shadowbans. We may disqualify entries, withhold rewards, or remove accounts that break these rules or manipulate any reward system.',
      'This site is not a casino. All wagering happens on shuffle.com, which has its own terms, account rules and restricted territories. Site rewards are promotional, funded by the affiliate commission SNUTZY earns from Shuffle, and are separate from anything Shuffle itself offers.',
      'Rewards and programs (leaderboard prizes, wager rewards, giveaways, lossback, points, referrals, clip rewards) can change or end between periods. Changes never remove rewards you have already been confirmed to have won.',
    ],
  },
  {
    id: 'rewards',
    title: 'Leaderboard & Rewards Terms',
    body: [
      'How wagering is counted — leaderboard positions and wager-based rewards use weighted wagers reported by Shuffle’s affiliate API: games with RTP of 98% or less count at 1×, RTP between 98–99% counts at 0.5×, and RTP of 99% or higher (originals, live games, blackjack) counts at 0.1×. Only play under guild SNUTZY counts.',
      'Periods — the monthly leaderboard runs from the 24th of one month to the 24th of the next, at midnight UK time. Standings shown during a period are live estimates; final placements are taken at the reset.',
      'Eligibility — you must be 18 or older, have a snutzy.gg account with the Shuffle username you play under, and be located somewhere Shuffle legally serves (see Shuffle’s terms for their restricted territories).',
      'Payouts — rewards are reviewed and paid manually by the team, usually as Shuffle balance tips. Most claims are handled within a few days. Lossback claims are cross-checked against Shuffle stats before payout and take longer than other rewards.',
      'Exclusions — accounts flagged for fraud (multi-accounting, self-referrals, VPN abuse to fake eligibility, collusion) forfeit pending rewards while the flag is investigated. We may request proof of identity or account ownership before paying large rewards.',
      'Disputes — if you believe a reward was counted or paid wrongly, open a ticket in the Discord. Include your site username, Shuffle username and the reward in question. Decisions after review are final.',
    ],
  },
  {
    id: 'privacy',
    title: 'Privacy',
    body: [
      'Who we are — snutzy.gg is operated from the United Kingdom as the community site for the SNUTZY stream. This section explains what personal data the site holds, why, and how to get it removed.',
      'Account data — signing in uses your Twitch account via OAuth. We store your Twitch ID, username, display name and avatar; we never see your Twitch password. If you link them, we also store the Shuffle username you provide and your Discord ID. Legal basis: performing the service you signed up for.',
      'Participation records — your predictions, giveaway entries, points balance and history, reward claims, referrals and watchtime. These power your profile, the reward systems and public winner lists. Legal basis: performing the service.',
      'Lossback screenshots — PNL screenshots you upload with a lossback claim are stored so the claim can be verified against Shuffle stats, and are never public.',
      'Security data — your IP address, approximate country, and a device fingerprint (browser and hardware characteristics), including VPN/datacenter signals. These are used solely to detect multi-account abuse and keep rewards fair, and they are never public. Legal basis: legitimate interest in preventing fraud.',
      'Wager data — your wager totals come to us already aggregated from Shuffle’s affiliate API. We never see your individual bets, deposits or balance.',
      'What we do not do — we do not sell your data and we do not run advertising trackers. The only thing stored in your browser is your login token. Data lives on our hosting providers (Railway for the database and API, Vercel for the site) and traffic passes through Cloudflare.',
      'Your rights — to have your account and data removed, or to ask what we hold about you, open a ticket in the Discord. Public winner lists keep the masked winner name for transparency, but the link to your account is severed on deletion. You also have the right to complain to the ICO (ico.org.uk).',
      'Retention — account and participation data is kept while your account exists. Security data is kept only as long as needed for fraud prevention. Lossback screenshots are kept for claim verification and audit.',
    ],
  },
  {
    id: 'responsible-gambling',
    title: 'Responsible Gambling',
    body: [
      'Gambling is entertainment, not income. SNUTZY streams real play with real losses — the leaderboard measures activity, not success. Never bet money you cannot afford to lose, and never chase losses.',
      'This site and everything it rewards is for viewers 18 and over. Shuffle offers self-exclusion and limit tools on your casino account — use them if you need distance.',
      'If gambling stops being fun, help is free and confidential: BeGambleAware.org (UK) and GamblingTherapy.org (worldwide), 24/7.',
    ],
  },
];

// /terms, /privacy, /responsible-gambling land here too — map them to anchors
const PATH_ANCHORS = {
  '/terms': 'terms',
  '/privacy': 'privacy',
  '/responsible-gambling': 'responsible-gambling',
  '/rewards-terms': 'rewards',
};

export default function LegalPage() {
  useEffect(() => {
    const fromHash = (window.location.hash || '').replace('#', '');
    const fromPath = PATH_ANCHORS[window.location.pathname.toLowerCase()];
    const target = fromHash || fromPath;
    if (target) {
      // Let the page paint first so the scroll position is correct
      requestAnimationFrame(() => {
        document.getElementById(target)?.scrollIntoView({ block: 'start' });
      });
    }
  }, []);

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: '2.5rem 1.5rem 4rem' }}>
      <h1 style={{ fontFamily: "'Unbounded', 'Outfit', sans-serif", fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)', fontWeight: 800, marginBottom: '0.4rem' }}>
        Legal
      </h1>
      <p style={{ color: '#71717a', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Last updated: {LAST_UPDATED}</p>
      <nav aria-label="Legal sections" style={{ display: 'flex', gap: '0.5rem 1.25rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {sections.map((s) => (
          <a key={s.id} href={`#${s.id}`} style={{ color: '#c4b5fd', fontSize: '0.85rem', textDecoration: 'underline', textUnderlineOffset: '3px' }}>
            {s.title}
          </a>
        ))}
      </nav>

      {sections.map((s) => (
        <section key={s.id} id={s.id} style={{ marginTop: '2.75rem', scrollMarginTop: '90px' }}>
          <h2 style={{ fontFamily: "'Unbounded', 'Outfit', sans-serif", fontSize: '1.15rem', fontWeight: 700, color: '#c084fc', marginBottom: '0.5rem' }}>
            {s.title}
          </h2>
          {s.body.map((p, i) => (
            <p key={i} style={{ color: '#a1a1aa', fontSize: '0.92rem', lineHeight: 1.75, marginTop: '0.9rem' }}>
              {p}
            </p>
          ))}
        </section>
      ))}

      <p style={{ marginTop: '3rem', color: '#a1a1aa', fontSize: '0.85rem', lineHeight: 1.75 }}>
        Struggling with gambling?{' '}
        <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer" style={{ color: '#c4b5fd' }}>
          BeGambleAware.org
        </a>{' '}
        — free, confidential support, 24/7.
      </p>
    </div>
  );
}
